import { useQuery } from '@tanstack/react-query';
import { Alert, DatePicker, Empty, Form, Modal, Radio, Segmented, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { useApiMutation } from '@/api/hooks';
import { CATEGORY_LABEL, WAYBILL_STATUS_LABEL } from '@/lib/labels';
import {
  ClearanceOption,
  allowedFirst,
  type ClearanceStatus,
} from '@/pages/waybills/WaybillFormModal';

import { routeText, type TransportRequestRow } from './types';

const { RangePicker } = DatePicker;

interface Props {
  request: TransportRequestRow | null;
  onClose: () => void;
}

interface WaybillOption {
  id: number;
  number: string;
  status: string;
  validFrom: string;
  validTo: string;
  vehicle: { garageNumber: string; plateNumber: string | null; category: string };
  driver: { employee: { lastName: string; firstName: string } };
}

interface VehicleOption {
  id: number;
  garageNumber: string;
  plateNumber: string | null;
  category: string;
}

/**
 * Период нового листа по умолчанию — смена, в которую попадает подача
 * машины (дневная 08:00–20:00 или ночная 20:00–08:00), а не время одной
 * заявки: иначе следующая заявка этой машины на ту же смену не нашла бы
 * лист и ушла бы в новый.
 */
function shiftAround(from: dayjs.Dayjs, to: dayjs.Dayjs | null): [dayjs.Dayjs, dayjs.Dayjs] {
  const hour = from.hour();
  const start =
    hour >= 8 && hour < 20
      ? from.hour(8).startOf('hour')
      : hour >= 20
        ? from.hour(20).startOf('hour')
        : from.subtract(1, 'day').hour(20).startOf('hour');
  const end = start.add(12, 'hour');
  return [start, to && to.isAfter(end) ? to : end];
}

/**
 * Назначение заявки: заданием в уже оформленный путевой лист (машина за
 * смену выполняет несколько заявок) или новым листом на выбранную машину и
 * водителя. Новый лист проходит те же проверки допуска, что и созданный из
 * журнала путевых листов, — отметки в списках показывают их заранее.
 */
export function AssignRequestModal({ request, onClose }: Props) {
  const { t } = useTranslation();
  const open = request !== null;
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [waybillId, setWaybillId] = useState<number | null>(null);
  const [form] = Form.useForm<{
    vehicleId: number;
    driverId: number;
    period: [dayjs.Dayjs, dayjs.Dayjs];
  }>();

  const options = useQuery({
    queryKey: ['transport-request-waybills', request?.id],
    enabled: open,
    queryFn: async () =>
      (await api.get<WaybillOption[]>(`/transport-requests/${request!.id}/waybill-options`)).data,
  });

  const vehicles = useQuery({
    queryKey: ['vehicles-lookup-assign'],
    enabled: open && mode === 'new',
    queryFn: async () =>
      (await api.get<{ items: VehicleOption[] }>('/vehicles', { params: { pageSize: 200, status: 'ACTIVE' } }))
        .data,
  });
  const drivers = useQuery({
    queryKey: ['drivers-lookup'],
    enabled: open && mode === 'new',
    queryFn: async () =>
      (
        await api.get<{
          items: Array<{
            id: number;
            employee: { lastName: string; firstName: string; personnelNumber: string };
          }>;
        }>('/drivers', { params: { pageSize: 200, isActive: true } })
      ).data,
  });
  const technicalQueue = useQuery({
    queryKey: ['technical-queue-lookup'],
    enabled: open && mode === 'new',
    queryFn: async () =>
      (await api.get<Array<ClearanceStatus & { vehicleId: number }>>('/technical/queue')).data,
  });
  const medicalQueue = useQuery({
    queryKey: ['medical-queue-lookup'],
    enabled: open && mode === 'new',
    queryFn: async () =>
      (await api.get<Array<ClearanceStatus & { driverId: number }>>('/medical/queue')).data,
  });
  const vehicleClearance = new Map((technicalQueue.data ?? []).map((row) => [row.vehicleId, row]));
  const driverClearance = new Map((medicalQueue.data ?? []).map((row) => [row.driverId, row]));

  // Открылось окно — если подходящих листов нет, сразу предлагаем новый.
  useEffect(() => {
    if (!open) return;
    setWaybillId(null);
    form.resetFields();
    form.setFieldsValue({
      period: shiftAround(dayjs(request.neededFrom), request.neededTo ? dayjs(request.neededTo) : null),
    });
  }, [open, request, form]);

  useEffect(() => {
    if (options.data) setMode(options.data.length > 0 ? 'existing' : 'new');
  }, [options.data]);

  const assign = useApiMutation(
    async (body: Record<string, unknown>) =>
      (await api.post(`/transport-requests/${request!.id}/assign`, body)).data,
    {
      successMessage: t('Заявка назначена'),
      invalidate: [['transport-requests'], ['waybills']],
    },
  );

  const selected = options.data?.find((w) => w.id === waybillId);
  const alreadyIssued = selected !== undefined && selected.status !== 'DRAFT';

  const submit = (): void => {
    if (mode === 'existing') {
      if (waybillId) assign.mutate({ waybillId }, { onSuccess: onClose });
      return;
    }
    void form.validateFields().then((values) =>
      assign.mutate(
        {
          vehicleId: values.vehicleId,
          driverId: values.driverId,
          validFrom: values.period[0].toISOString(),
          validTo: values.period[1].toISOString(),
        },
        { onSuccess: onClose },
      ),
    );
  };

  // Машины нужной категории — первыми: диспетчеру не надо искать их по списку.
  const wanted = request?.vehicleCategory ?? null;
  const vehicleItems = allowedFirst(
    [...(vehicles.data?.items ?? [])].sort(
      (a, b) => Number(b.category === wanted) - Number(a.category === wanted),
    ),
    (v) => vehicleClearance.get(v.id),
  );

  return (
    <Modal
      open={open}
      title={request ? `${t('Назначить заявку')} ${request.number}` : ''}
      okText={t('Назначить')}
      cancelText={t('Отмена')}
      width={720}
      confirmLoading={assign.isPending}
      okButtonProps={{ disabled: mode === 'existing' && !waybillId }}
      onCancel={onClose}
      onOk={submit}
      destroyOnHidden
    >
      {request && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={`${dayjs(request.neededFrom).format('DD.MM.YYYY HH:mm')}${request.neededTo ? ` — ${dayjs(request.neededTo).format('HH:mm')}` : ''} · ${routeText(request)}`}
            description={[
              request.vehicleCategory ? t(CATEGORY_LABEL[request.vehicleCategory] ?? request.vehicleCategory) : null,
              request.passengers ? `${t('Пассажиров')}: ${request.passengers}` : null,
              request.cargoTons ? `${t('Груз, т')}: ${Number(request.cargoTons)}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || undefined}
          />

          <Segmented
            block
            value={mode}
            onChange={(value) => setMode(value as 'existing' | 'new')}
            options={[
              { value: 'existing', label: `${t('В оформленный лист')} (${options.data?.length ?? 0})` },
              { value: 'new', label: t('Новый путевой лист') },
            ]}
          />

          {mode === 'existing' ? (
            options.data && options.data.length === 0 ? (
              <Empty description={t('На это время нет оформленных путевых листов')} />
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Radio.Group
                value={waybillId}
                onChange={(event) => setWaybillId(event.target.value as number)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {(options.data ?? []).map((w) => (
                    <Radio key={w.id} value={w.id}>
                      <Space size={8} wrap>
                        <Typography.Text strong>{w.number}</Typography.Text>
                        <Tag bordered={false}>{t(WAYBILL_STATUS_LABEL[w.status] ?? w.status)}</Tag>
                        <span>
                          {w.vehicle.garageNumber}
                          {w.vehicle.plateNumber ? ` · ${w.vehicle.plateNumber}` : ''} ·{' '}
                          {w.driver.employee.lastName} {w.driver.employee.firstName}
                        </span>
                        <Typography.Text type="secondary">
                          {dayjs(w.validFrom).format('DD.MM HH:mm')} — {dayjs(w.validTo).format('DD.MM HH:mm')}
                        </Typography.Text>
                        {wanted && w.vehicle.category === wanted && (
                          <Tag color="success" bordered={false}>
                            {t('нужная категория')}
                          </Tag>
                        )}
                      </Space>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              {alreadyIssued && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('Лист уже выдан водителю — после назначения перепечатайте его и сообщите водителю о новом задании.')}
                />
              )}
              </Space>
            )
          ) : (
            <Form form={form} layout="vertical" requiredMark="optional">
              <Form.Item
                name="vehicleId"
                label={t('Техника')}
                rules={[{ required: true, message: t('Выберите технику') }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={vehicles.isLoading}
                  options={vehicleItems.map((v) => ({
                    value: v.id,
                    label: `${v.garageNumber}${v.plateNumber ? ` · ${v.plateNumber}` : ''} — ${t(CATEGORY_LABEL[v.category] ?? v.category)}`,
                  }))}
                  optionRender={(option) => (
                    <ClearanceOption
                      text={String(option.label)}
                      status={vehicleClearance.get(Number(option.value))}
                    />
                  )}
                />
              </Form.Item>
              <Form.Item
                name="driverId"
                label={t('Водитель')}
                rules={[{ required: true, message: t('Выберите водителя') }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={drivers.isLoading}
                  options={allowedFirst(drivers.data?.items ?? [], (d) => driverClearance.get(d.id)).map(
                    (d) => ({
                      value: d.id,
                      label: `${d.employee.lastName} ${d.employee.firstName} (${d.employee.personnelNumber})`,
                    }),
                  )}
                  optionRender={(option) => (
                    <ClearanceOption
                      text={String(option.label)}
                      status={driverClearance.get(Number(option.value))}
                    />
                  )}
                />
              </Form.Item>
              <Form.Item
                name="period"
                label={t('Период путевого листа')}
                rules={[{ required: true, message: t('Укажите период') }]}
              >
                <RangePicker showTime={{ format: 'HH:mm' }} format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
              <Typography.Text type="secondary">
                {t('Будет создан черновик путевого листа с заданием по этой заявке. Выдать его водителю можно в журнале путевых листов.')}
              </Typography.Text>
            </Form>
          )}
        </Space>
      )}
    </Modal>
  );
}
